import { useEffect, useRef, useState } from "react";

/** Confirmaciones DENTRO de la página (Eduardo 2026-09-18).
 *
 * Sustituyen a `window.confirm` / `window.alert`: el diálogo nativo bloquea el
 * hilo (un agente de navegador lo ve como pestaña congelada) y en el celular
 * se ve ajeno a la app.
 *
 * REGLA DE USO (pedido explícito de Eduardo): un diálogo SOLO se abre como
 * respuesta a algo que la persona acaba de intentar (un clic en Eliminar,
 * Publicar, Reservar…). Nunca desde un efecto de carga ni "porque sí": si no
 * hay una acción previa, lo correcto es un mensaje en pantalla, no un diálogo.
 * Por eso `askConfirm` se llama únicamente desde manejadores `handle*`.
 */

export type ConfirmTone = "default" | "danger";

export interface ConfirmOptions {
  /** Verbo de la acción: "Sí, eliminar partido". Nunca un "Aceptar" genérico. */
  acceptLabel?: string;
  /** "danger" pinta el botón de rojo y deja el foco en "No, regresar". */
  tone?: ConfirmTone;
  /** Texto del botón que NO hace nada. Evita "Cancelar", ambiguo cuando la
   * propia acción es cancelar algo ("¿Cancelar la reserva?"). */
  dismissLabel?: string;
  /** Al abortarse, la pregunta se retira (si sigue en pantalla) y resuelve
   * «no». Para la pantalla que se desmonta o cambia de recurso mientras el
   * diálogo, que es global, sigue abierto. Vale en `askConfirm`, `askChoice`
   * y `askConfirmWithChecks`. */
  signal?: AbortSignal;
}

/** Qué botón pulsó la persona en un diálogo con tres salidas (`askChoice`). */
export type ConfirmChoice = "accept" | "secondary" | "dismiss";

/** Casilla opcional dentro de la confirmación (`askConfirmWithChecks`). */
export interface ConfirmCheck {
  key: string;
  label: string;
}

interface ConfirmRequest {
  id: number;
  message: string;
  acceptLabel: string;
  /** Segunda acción opcional (solo `askChoice`). */
  secondaryLabel: string | null;
  dismissLabel: string | null;
  tone: ConfirmTone;
  /** Casillas opcionales; siempre empiezan sin marcar. */
  checks?: ConfirmCheck[];
  resolve: (choice: ConfirmChoice, checked: string[]) => void;
}

let nextId = 1;
let queue: ConfirmRequest[] = [];
let notify: (() => void) | null = null;

function enqueue(request: Omit<ConfirmRequest, "id">): number {
  const id = nextId++;
  queue = [...queue, { ...request, id }];
  notify?.();
  return id;
}

/** Encola una pregunta que el `signal` puede retirar: contesta «no» y deja
 * en paz a las demás de la fila. */
function enqueueAbortable(request: Omit<ConfirmRequest, "id">, signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    request.resolve("dismiss", []);
    return;
  }
  const onAbort = () => {
    const pending = queue.find((item) => item.id === requestId);
    if (!pending) return;
    queue = queue.filter((item) => item.id !== requestId);
    pending.resolve("dismiss", []);
    notify?.();
  };
  const requestId = enqueue({
    ...request,
    resolve: (choice, checked) => {
      signal?.removeEventListener("abort", onAbort);
      request.resolve(choice, checked);
    },
  });
  signal?.addEventListener("abort", onAbort, { once: true });
}

/** Pregunta antes de ejecutar una acción. Resuelve `true` solo si la persona
 * pulsa el botón de la acción. */
export function askConfirm(message: string, options: ConfirmOptions = {}): Promise<boolean> {
  // Sin anfitrión montado (pruebas unitarias, render aislado): se conserva el
  // comportamiento anterior para no resolver "sí" a ciegas.
  if (!notify) {
    const native = typeof window !== "undefined" && typeof window.confirm === "function";
    return Promise.resolve(native ? window.confirm(message) : false);
  }
  return new Promise((resolve) => {
    enqueueAbortable({
      message,
      acceptLabel: options.acceptLabel ?? "Sí, continuar",
      secondaryLabel: null,
      dismissLabel: options.dismissLabel ?? "No, regresar",
      tone: options.tone ?? "default",
      resolve: (choice) => resolve(choice === "accept"),
    }, options.signal);
  });
}

/** Como `askConfirm`, pero con una SEGUNDA acción: resuelve "accept",
 * "secondary" o "dismiss". Para cuando hay dos caminos válidos tras un clic
 * (p. ej. «Acceso propio» o cambiar la contraseña de la cuenta familiar). */
export function askChoice(
  message: string,
  options: ConfirmOptions & { acceptLabel: string; secondaryLabel: string },
): Promise<ConfirmChoice> {
  if (!notify) {
    const native = typeof window !== "undefined" && typeof window.confirm === "function";
    return Promise.resolve(native && window.confirm(message) ? "accept" : "dismiss");
  }
  return new Promise((resolve) => {
    enqueueAbortable({
      message,
      acceptLabel: options.acceptLabel,
      secondaryLabel: options.secondaryLabel,
      dismissLabel: options.dismissLabel ?? "No, regresar",
      tone: options.tone ?? "default",
      resolve,
    }, options.signal);
  });
}

/** Como `askConfirm`, con casillas que empiezan SIN marcar (p. ej. «También
 * dar de baja: Categoría 3»). Resuelve si se aceptó y qué casillas quedaron
 * marcadas; al regresar no se marca nada. */
export function askConfirmWithChecks(
  message: string,
  options: ConfirmOptions & { checks: ConfirmCheck[] },
): Promise<{ accepted: boolean; checked: string[] }> {
  if (!notify) {
    const native = typeof window !== "undefined" && typeof window.confirm === "function";
    return Promise.resolve({ accepted: native ? window.confirm(message) : false, checked: [] });
  }
  return new Promise((resolve) => {
    enqueueAbortable({
      message,
      acceptLabel: options.acceptLabel ?? "Sí, continuar",
      secondaryLabel: null,
      dismissLabel: options.dismissLabel ?? "No, regresar",
      tone: options.tone ?? "default",
      checks: options.checks,
      resolve: (choice, checked) => resolve(
        choice === "accept" ? { accepted: true, checked } : { accepted: false, checked: [] },
      ),
    }, options.signal);
  });
}

/** Aviso de un solo botón ("Entendido") tras una acción que terminó a medias. */
export function showNotice(message: string, acceptLabel = "Entendido"): Promise<void> {
  if (!notify) {
    if (typeof window !== "undefined" && typeof window.alert === "function") window.alert(message);
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    enqueue({ message, acceptLabel, secondaryLabel: null, dismissLabel: null, tone: "default", resolve: () => resolve() });
  });
}

/** Anfitrión único, montado en la raíz de la app. */
export function ConfirmDialogHost() {
  const [, setTick] = useState(0);
  const [checked, setChecked] = useState<string[]>([]);
  const acceptRef = useRef<HTMLButtonElement | null>(null);
  const dismissRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    notify = () => setTick((value) => value + 1);
    return () => {
      notify = null;
      // Al desmontar nadie puede contestar: las preguntas pendientes se niegan.
      for (const pending of queue) pending.resolve("dismiss", []);
      queue = [];
    };
  }, []);

  const current = queue[0] ?? null;

  function settle(choice: ConfirmChoice) {
    if (!current) return;
    // Por id, no «la primera de la fila»: si `current` (del último render) ya
    // se retiró por su señal, quitar la primera se llevaba la SIGUIENTE
    // pregunta sin contestarla. Una pregunta retirada ya contestó «no».
    const settled = current;
    if (!queue.some((item) => item.id === settled.id)) {
      setTick((value) => value + 1);
      return;
    }
    queue = queue.filter((item) => item.id !== settled.id);
    settled.resolve(choice, choice === "accept" ? checked : []);
    setChecked([]);
    setTick((value) => value + 1);
  }

  useEffect(() => {
    // Una pregunta retirada no pasa por `settle`: sus marcas no se heredan.
    setChecked((value) => (value.length > 0 ? [] : value));
    if (!current) return;
    // Acción destructiva: el foco cae en el botón que no hace nada.
    const target = current.tone === "danger" && current.dismissLabel ? dismissRef.current : acceptRef.current;
    target?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        settle(current.dismissLabel === null ? "accept" : "dismiss");
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  if (!current) return null;

  return (
    <div className="inline-confirm" role="alertdialog" aria-modal="true" aria-label="Confirmar acción">
      <div className="inline-confirm__card">
        {current.message.split("\n").filter((line) => line.trim()).map((line, index) => (
          <p key={index}>{line}</p>
        ))}
        {current.checks && current.checks.length > 0 ? (
          <div className="inline-confirm__checks">
            {current.checks.map((check) => (
              <label key={check.key}>
                <input
                  type="checkbox"
                  checked={checked.includes(check.key)}
                  onChange={(event) => {
                    const on = event.target.checked;
                    setChecked((value) => (on ? [...value, check.key] : value.filter((key) => key !== check.key)));
                  }}
                />
                {check.label}
              </label>
            ))}
          </div>
        ) : null}
        <div className="inline-confirm__actions">
          {current.dismissLabel !== null ? (
            <button ref={dismissRef} type="button" className="btn btn--ghost" onClick={() => settle("dismiss")}>
              {current.dismissLabel}
            </button>
          ) : null}
          {current.secondaryLabel !== null ? (
            <button type="button" className="btn btn--ghost" onClick={() => settle("secondary")}>
              {current.secondaryLabel}
            </button>
          ) : null}
          <button
            ref={acceptRef}
            type="button"
            className={`btn${current.tone === "danger" ? " btn--danger" : ""}`}
            onClick={() => settle("accept")}
          >
            {current.acceptLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
